import SearchQueryBuilder from '../utils/search-query-builder';

const PAGE_SIZE = 12;

export default function RecordingSourceChooserBrowseController(
  $uibModalInstance,
  $q,
  $http,
  Broadcast,
  Recording,
  toaster,
  options
) {
  'ngInject';

  // Parse options passed in from `resolve`
  this.skipRecordingSourceValidation = options.skipRecordingSourceValidation;
  this.modalTitle = options.modalTitle || 'Choose Source Broadcast';
  this.activeTab = options.activeTabIndex || 0;

  this.currentSearchText = '';
  this.searchText = '';
  this.searching = false;
  this.broadcasts = {
    upcoming: {
      $promise: null,
      data: [],
      pagination: {
        current: 1,
        resultMin: 0,
        resultMax: 0,
      },
    },
    current: {
      $promise: null,
      data: [],
      pagination: {
        current: 1,
        resultMin: 0,
        resultMax: 0,
      },
    },
    past: {
      $promise: null,
      data: [],
      pagination: {
        current: 1,
        resultMin: 0,
        resultMax: 0,
      },
    },
  };

  this.cancel = () => $uibModalInstance.dismiss();

  this.eventCount = (broadcast) => {
    if (broadcast.pagination && broadcast.pagination.total) {
      return `${broadcast.pagination.total}`;
    } else {
      return broadcast.data.length;
    }
  };

  this.loadBroadcast = (timeframe) => {
    // Load the next page of events for the given broadcast set, and populate
    // the `this.broadcasts` data structure accordingly.
    // @param timeframe: one of 'upcoming', 'current', or 'past'
    let broadcast = this.broadcasts[timeframe];

    let q = new SearchQueryBuilder();
    let s = 'starts_at'; //default sort order
    switch (timeframe) {
      case 'upcoming':
        q.append('timeframe:future');
        break;
      case 'current':
        q.append('timeframe:preroll').append('timeframe:current');
        break;
      case 'past':
        q.append('timeframe:past');
        s = '-starts_at'; //reverse sort
        break;
    }

    // NOTE: Current events tab is never filtered, as it's not expected to have
    // more than a handful ongoing and you wouldn't want to hide them anyway
    if (this.searchText && timeframe != 'current') {
      q.appendSearchText(this.searchText);
    }

    let query = Broadcast.query(
      {
        q: q.toString(),
        s: s,
        p: broadcast.pagination.current - 1,
        l: PAGE_SIZE,
      },
      (data, headers) => {
        this.currentSearchText = this.searchText;
        broadcast.data = data;
        let pagination = JSON.parse(headers('x-pagination'));

        broadcast.pagination.total = pagination && pagination.total;
        broadcast.pagination.resultMin = PAGE_SIZE * (broadcast.pagination.current - 1) + 1;
        broadcast.pagination.resultMax = broadcast.pagination.resultMin + data.length - 1;

        // If current tab is selected but there's nothing in there, need to kick
        // user somewhere else (it's the only one that disappears)
        if (timeframe == 'current' && this.activeTab === 0 && !data.length) {
          //console.log('Switching from current tab to upcoming since no more data', this.broadcasts);
          this.activeTab = 1;
        }
      },
      (e) => {
        console.error(e);
        toaster.pop('warning', 'Unable to load broadcasts. Please refresh the page and try again');
      }
    );

    broadcast.$promise = query.$promise;
    this.loadingPromises = [query.$promise];
  };

  this.reload = () => {
    _.each(this.broadcasts, (n, key) => {
      this.loadBroadcast(key);
    });
    this.loadingPromises = [
      this.broadcasts.upcoming.$promise,
      this.broadcasts.current.$promise,
      this.broadcasts.past.$promise,
    ];
  };

  this.search = () => {
    // Reset pagination and data
    _.each(this.broadcasts, (n, key) => {
      let broadcast = this.broadcasts[key];
      broadcast.pagination.current = 1;
      broadcast.pagination.total = 0;
    });

    // Apply search term
    this.reload();

    // Handle loading spinner
    this.searching = true;
    $q.all([
      this.broadcasts.upcoming.$promise,
      this.broadcasts.current.$promise,
      this.broadcasts.past.$promise,
    ]).finally(() => (this.searching = false));
  };

  this.select = (broadcast) => {
    this.loadingPromises = [
      Broadcast.get(
        { id: broadcast.id },
        (b) => {
          if (options.broadcastValidator) {
            try {
              options.broadcastValidator(b);
              $uibModalInstance.close(b);
            } catch (e) {
              toaster.pop('warning', e.toString());
            }
          } else if (this.skipRecordingSourceValidation) {
            $uibModalInstance.close(b);
          } else if (b.timeframe != 'past') {
            if (b.stream_source == 'recording') {
              toaster.pop('warning', 'Please choose a non-simulated live broadcast.');
            } else {
              $uibModalInstance.close(b);
            }
          } else if (b.recording_duration_seconds > 0) {
            const segments = Recording.segments({ id: b.recording_id });
            this.loadingPromises = [segments.$promise];
            segments.$promise
              .then(() => {
                if (_.some(segments, (s) => s.byte_ranges)) {
                  $uibModalInstance.close(b);
                } else {
                  toaster.pop(
                    'warning',
                    "This broadcast's recording is not in the correct format. Please choose a newer broadcast, or upload a recording."
                  );
                }
              })
              .catch((e) => {
                console.error(e);
                toaster.pop(
                  'warning',
                  "This broadcast's recording could not be found, or is not in the correct format."
                );
              });
          } else {
            // Check to see if this is being uploaded
            if (!b.recording_id && b.stream_source == 'imported') {
              toaster.pop(
                'warning',
                'This broadcast is currently being uploaded; please try again when processing is complete.'
              );
              return;
            }

            let uploadsPromise = $http.get(
              `${API_ROOT}/account/broadcasts/${broadcast.id}/uploads`
            );
            this.loadingPromises = [uploadsPromise];
            uploadsPromise
              .then((response) => {
                if (!response || !response.data || !response.data.length) {
                  toaster.pop('warning', 'Please choose a broadcast with a valid recording.');
                  return;
                }

                let upload = response.data[0] || {};
                if (
                  b.recording_id &&
                  ['created', 'uploaded', 'assigned', 'processing', 'failed'].indexOf(
                    upload.status
                  ) >= 0
                ) {
                  toaster.pop(
                    'warning',
                    'This broadcast is currently being uploaded; please try again when processing is complete.'
                  );
                  return;
                }

                toaster.pop('warning', 'Please choose a broadcast with a valid recording.');
              })
              .catch((e) => {
                console.error(e);
                toaster.pop('warning', "This broadcast's uploads could not be found.");
              });
          }
        },
        (e) => {
          console.error(e);
          toaster.pop('warning', 'Unable to load broadcast. Please refresh the page and try again');
        }
      ).$promise,
    ];

    // This method returns true to let <broadcast-card/> know that we dealt with the selection and
    // it doesn't need to navigate / change state.
    return true;
  };

  this.reload();
}



// WEBPACK FOOTER //
// ./app/directives/recording-source-chooser-browse.js